import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { User, Mail, Phone, Building2, Shield, KeyRound, Lock, LogOut, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from '../../hooks/use-toast';
import { portalPath } from '../../lib/portal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GovProfile = () => {
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();
  const [pw, setPw] = useState({ old_password: '', new_password: '', confirm: '' });
  const [submitting, setSubmitting] = useState(false);
  const [agency, setAgency] = useState(null);

  useEffect(() => { document.title = 'My profile · USKE Procurement Portal'; }, []);

  const fetchAgency = useCallback(async () => {
    if (!user?.agency_id) return;
    try {
      const res = await axios.get(`${API}/gov/agencies/${user.agency_id}`);
      setAgency(res.data);
    } catch (e) { /* non-blocking */ }
  }, [user]);

  useEffect(() => { fetchAgency(); }, [fetchAgency]);

  const submitChange = async (e) => {
    e.preventDefault();
    if (pw.new_password.length < 8) return toast({ title: 'Password too short', description: 'Use at least 8 characters.', variant: 'destructive' });
    if (pw.new_password !== pw.confirm) return toast({ title: "Passwords don't match", variant: 'destructive' });
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/change-password`, { old_password: pw.old_password, new_password: pw.new_password });
      toast({ title: 'Password updated' });
      setPw({ old_password: '', new_password: '', confirm: '' });
      if (user) login({ ...user, must_change_password: false }, localStorage.getItem('token'));
    } catch (err) {
      toast({ title: 'Update failed', description: err.response?.data?.detail || 'Check your current password', variant: 'destructive' });
    } finally { setSubmitting(false); }
  };

  if (!user) return null;
  const role = user.role || 'unknown';

  return (
    <div className="space-y-4 max-w-3xl" data-testid="gov-profile-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 flex items-center gap-2">
          <User className="h-7 w-7 text-blue-700" /> My Profile
        </h1>
        <p className="text-sm text-slate-500">Account details and password.</p>
      </div>

      {/* Account info */}
      <Card className="border-2">
        <CardHeader className="pb-2"><CardTitle className="text-base">Account</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-6 text-sm">
          <div><span className="text-slate-400 text-xs uppercase block">Name</span> <span className="text-slate-800 font-medium">{user.name || '—'}</span></div>
          <div><span className="text-slate-400 text-xs uppercase block">Role</span> <span className="text-slate-800 font-medium uppercase text-xs tracking-wide bg-blue-50 text-blue-700 px-2 py-0.5 rounded inline-block">{role.replace('_', ' ')}</span></div>
          <div className="flex items-start gap-2"><Mail className="h-3.5 w-3.5 text-slate-400 mt-0.5" /><div><span className="text-slate-400 text-xs uppercase block">Email</span> <span className="text-slate-800">{user.email}</span></div></div>
          {user.phone && <div className="flex items-start gap-2"><Phone className="h-3.5 w-3.5 text-slate-400 mt-0.5" /><div><span className="text-slate-400 text-xs uppercase block">Phone</span> <span className="text-slate-800">{user.phone}</span></div></div>}
          {(agency || user.agency_id) && (
            <div className="sm:col-span-2 flex items-start gap-2 pt-2 border-t border-slate-100"><Building2 className="h-3.5 w-3.5 text-slate-400 mt-0.5" /><div><span className="text-slate-400 text-xs uppercase block">Organisation</span> <span className="text-slate-800">{agency?.name || user.agency_id}</span></div></div>
          )}
        </CardContent>
      </Card>

      {/* Two-factor */}
      <Card className="border-2">
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Shield className="h-4 w-4 text-blue-700" /> Two-factor authentication</CardTitle></CardHeader>
        <CardContent className="text-sm space-y-2">
          {user.mfa_enabled ? (
            <div className="flex items-center gap-2 text-emerald-700"><CheckCircle2 className="h-4 w-4" /> TOTP MFA enabled</div>
          ) : (
            <div className="flex items-center gap-2 text-amber-700"><XCircle className="h-4 w-4" /> TOTP MFA not enabled</div>
          )}
          <Link to={portalPath('/security')}>
            <Button variant="outline" size="sm" data-testid="gov-profile-mfa-link"><KeyRound className="h-3.5 w-3.5 mr-1" /> Manage MFA</Button>
          </Link>
        </CardContent>
      </Card>

      {/* Change password */}
      <Card className="border-2">
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Lock className="h-4 w-4 text-blue-700" /> Change password</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={submitChange} className="space-y-3">
            <div>
              <Label htmlFor="op">Current password</Label>
              <Input id="op" type="password" required value={pw.old_password} onChange={(e) => setPw({ ...pw, old_password: e.target.value })} data-testid="profile-old-password" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="np">New password <span className="text-xs text-slate-400">(min 8)</span></Label>
                <Input id="np" type="password" required minLength={8} value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="profile-new-password" />
              </div>
              <div>
                <Label htmlFor="cp">Confirm new password</Label>
                <Input id="cp" type="password" required value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} data-testid="profile-confirm-password" />
              </div>
            </div>
            <Button type="submit" disabled={submitting} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="profile-change-password-submit">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Lock className="h-4 w-4 mr-1" />} Update password
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Sign out */}
      <div>
        <Button variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50" onClick={async () => { await logout(); navigate(portalPath('/login')); }} data-testid="profile-signout">
          <LogOut className="h-4 w-4 mr-1" /> Sign out
        </Button>
      </div>
    </div>
  );
};

export default GovProfile;
